import {FC} from 'react';
import {t} from 'i18next';
import {translations} from '../../../i18n/translations';
import {CareTaskFeature} from '../../../backend/enums/care-task-feature';
import FlatInput from '../../flat-input/flat-input';
import {FlatSelectDropdown} from '../../flat-select-dropdown/flat-select-dropdown';
import {FlatStartsAtTime} from '../../flat-starts-at-time/flat-starts-at-time';
import {Option} from '../../select-dropdown/select-dropdown';
import {DailyCareInput} from '../../daily-care-log/forms/daily-care-input';
import {DailyCareSelect} from '../../daily-care-log/forms/daily-care-select';
import {DailyCareTimeInput} from '../../daily-care-log/forms/daily-care-time-input';
import {RegisterOptions} from 'react-hook-form';

type ComponentTypes = 'input' | 'select' | 'time';

interface FeatureFieldProps {
  component: ComponentTypes;
  props?: {
    type?: 'input' | 'number';
    label?: string;
    suffix?: string;
    placeholder?: string;
    options?: Option[];
    value?: string;
  };
  rules?: Omit<
    RegisterOptions<any, string>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
}

export const officeFeatureFieldTypes: Record<ComponentTypes, FC<any>> = {
  input: FlatInput,
  select: FlatSelectDropdown,
  time: FlatStartsAtTime,
};

export const dailyCareLogFeatureFieldTypes: Record<ComponentTypes, FC<any>> = {
  input: DailyCareInput,
  select: DailyCareSelect,
  time: DailyCareTimeInput,
};

const minPatternShape = (value: number, suffix: string) => {
  return {
    value,
    message: t(
      translations.care_and_activities.handle_task_modal.feature_input.number_pattern_validation
        .min,
      {
        min_suffix: `${value}${suffix}`,
      }
    ),
  };
};

const maxPatternShape = (value: number, suffix: string) => {
  return {
    value,
    message: t(
      translations.care_and_activities.handle_task_modal.feature_input.number_pattern_validation
        .max,
      {
        max_suffix: `${value}${suffix}`,
      }
    ),
  };
};

export const mappedFeatureFieldsIndex: Record<
  CareTaskFeature,
  Record<string, FeatureFieldProps>
> = {
  [CareTaskFeature.BodyTemperatureHealthMeasurement]: {
    temperatureInCelsius: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.label[
            CareTaskFeature.BodyTemperatureHealthMeasurement
          ]
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.value.placeholder
        ),
        type: 'number',
        suffix: 'ºC',
      },
      rules: {
        min: minPatternShape(33, 'ºC'),
        max: maxPatternShape(42, 'ºC'),
      },
    },
  },
  [CareTaskFeature.BodyWeightHealthMeasurement]: {
    weightInKilograms: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.label[
            CareTaskFeature.BodyWeightHealthMeasurement
          ]
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.value.placeholder
        ),
        type: 'number',
        suffix: 'kg',
      },
      rules: {
        min: minPatternShape(2, 'kg'),
        max: minPatternShape(360, 'kg'),
      },
    },
  },
  [CareTaskFeature.BodyHeightHealthMeasurement]: {
    heightInCentimeters: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.label[
            CareTaskFeature.BodyHeightHealthMeasurement
          ]
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.value.placeholder
        ),
        type: 'number',
        suffix: 'cm',
      },
      rules: {
        min: minPatternShape(30, 'cm'),
        max: maxPatternShape(300, 'cm'),
      },
    },
  },
  [CareTaskFeature.HeartRateHealthMeasurement]: {
    beatsPerMinute: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.label[
            CareTaskFeature.HeartRateHealthMeasurement
          ]
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.value.placeholder
        ),
        type: 'number',
        suffix: 'bpm',
      },
      rules: {
        min: minPatternShape(30, 'bpm'),
        max: maxPatternShape(350, 'bpm'),
      },
    },
  },
  [CareTaskFeature.BloodOxygenHealthMeasurement]: {
    percentage: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.percentage.label
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.percentage
            .placeholder
        ),
        type: 'number',
        suffix: '%',
      },
      rules: {
        min: minPatternShape(0, '%'),
        max: maxPatternShape(100, '%'),
      },
    },
  },
  [CareTaskFeature.BloodPressureHealthMeasurement]: {
    systolic: {
      component: 'input',
      props: {
        type: 'number',
        placeholder: 'SIS',
        suffix: 'mmHg',
      },
      rules: {
        min: minPatternShape(40, 'mmHg'),
        max: maxPatternShape(300, 'mmHg'),
      },
    },
    diastolic: {
      component: 'input',
      props: {
        type: 'number',
        placeholder: 'DIA',
        suffix: 'mmHg',
      },
      rules: {
        min: minPatternShape(30, 'mmHg'),
        max: maxPatternShape(200, 'mmHg'),
      },
    },
  },
  [CareTaskFeature.RespiratoryRateHealthMeasurement]: {
    breathsPerMinute: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.label[
            CareTaskFeature.RespiratoryRateHealthMeasurement
          ]
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.value.placeholder
        ),
        type: 'number',
        suffix: 'bpm',
      },
      rules: {
        min: minPatternShape(0, 'bpm'),
        max: maxPatternShape(60, 'bpm'),
      },
    },
  },
  [CareTaskFeature.BloodGlucoseHealthMeasurement]: {
    value: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.label[
            CareTaskFeature.BloodGlucoseHealthMeasurement
          ]
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.value.placeholder
        ),
        type: 'number',
        suffix: 'mg',
      },
      rules: {
        min: minPatternShape(0, 'mg'),
        max: maxPatternShape(900, 'mg'),
      },
    },
    mealTime: {
      component: 'select',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.mealTime.label
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.mealTime
            .placeholder
        ),
        options: [
          {
            key: 'before-meal',
            label: t(
              translations.care_and_activities.handle_task_modal.feature_input.inputs.mealTime
                .options['before-meal']
            ),
          },
          {
            key: 'after-meal',
            label: t(
              translations.care_and_activities.handle_task_modal.feature_input.inputs.mealTime
                .options['after-meal']
            ),
          },
        ],
      },
    },
  },
  [CareTaskFeature.BodyFatPercentageHealthMeasurement]: {
    percentage: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.percentage.label
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.percentage
            .placeholder
        ),
        type: 'number',
        suffix: '%',
      },
      rules: {
        min: minPatternShape(0, '%'),
        max: maxPatternShape(100, '%'),
      },
    },
  },
  [CareTaskFeature.BodyMusclePercentageHealthMeasurement]: {
    percentage: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.percentage.label
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.percentage
            .placeholder
        ),
        type: 'number',
        suffix: '%',
      },
      rules: {
        min: minPatternShape(0, '%'),
        max: maxPatternShape(100, '%'),
      },
    },
  },
  [CareTaskFeature.BodyWaterPercentageHealthMeasurement]: {
    percentage: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.percentage.label
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.percentage
            .placeholder
        ),
        type: 'number',
        suffix: '%',
      },
      rules: {
        min: minPatternShape(0, '%'),
        max: maxPatternShape(100, '%'),
      },
    },
  },
  [CareTaskFeature.NatriureticPeptideTestHealthMeasurement]: {
    bnp: {
      component: 'input',
      props: {
        label: t(translations.care_and_activities.handle_task_modal.feature_input.inputs.bnp.label),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.bnp.placeholder
        ),
        type: 'number',
        suffix: 'pg/mL',
      },
      rules: {
        min: minPatternShape(0, 'pg/mL'),
        max: maxPatternShape(2000, 'pg/mL'),
      },
    },
    nt_proBnp: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.nt_proBnp.label
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.nt_proBnp
            .placeholder
        ),
        type: 'number',
        suffix: 'pg/mL',
      },
      rules: {
        min: minPatternShape(0, 'pg/mL'),
        max: maxPatternShape(2000, 'pg/mL'),
      },
    },
  },
  [CareTaskFeature.StepsHealthMeasurement]: {
    value: {
      component: 'input',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.label[
            CareTaskFeature.StepsHealthMeasurement
          ]
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.value.placeholder
        ),
        type: 'number',
      },
      rules: {
        min: minPatternShape(
          0,
          ` ${t(translations.care_and_activities.handle_task_modal.feature_input.units.steps)}`
        ),
        max: maxPatternShape(
          90000,
          ` ${t(translations.care_and_activities.handle_task_modal.feature_input.units.steps)}`
        ),
      },
    },
  },
  [CareTaskFeature.SleepHealthMeasurement]: {
    type: {
      component: 'select',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.type.label
        ),
        placeholder: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.type.placeholder
        ),
        options: [
          {
            key: 'in-bed',
            label: t(
              translations.care_and_activities.handle_task_modal.feature_input.inputs.type.options[
                'in-bed'
              ]
            ),
          },
          {
            key: 'awake',
            label: t(
              translations.care_and_activities.handle_task_modal.feature_input.inputs.type.options[
                'awake'
              ]
            ),
          },
          {
            key: 'rem',
            label: t(
              translations.care_and_activities.handle_task_modal.feature_input.inputs.type.options[
                'rem'
              ]
            ),
          },
          {
            key: 'core',
            label: t(
              translations.care_and_activities.handle_task_modal.feature_input.inputs.type.options[
                'core'
              ]
            ),
          },
          {
            key: 'deep',
            label: t(
              translations.care_and_activities.handle_task_modal.feature_input.inputs.type.options[
                'deep'
              ]
            ),
          },
        ],
      },
    },
    startTime: {
      component: 'time',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.startTime.label
        ),
      },
    },
    endTime: {
      component: 'time',
      props: {
        label: t(
          translations.care_and_activities.handle_task_modal.feature_input.inputs.endTime.label
        ),
      },
    },
  },
};
